import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, AuthState, AUTH_ACTIONS } from '../reducers/auth';
import { DASHBOARD_ROUTE, LOGIN_ROUTE, THEME_ROUTE } from '../routes/types';
import { checkLoginAPI, loginAPI, logoutAPI } from '../services/auth';
import available_personas from '../data/navigation/available_personas.json';

export const AuthStore = createContext<
  [
    AuthState,
    (username: string, password: string) => any | undefined,
    () => any | undefined,
    string,
    (value: string) => any | undefined
  ]
>([
  { user: undefined, error: undefined, init: true, loading: false },
  () => {},
  () => {},
  'persona-hw',
  (value) => {},
]);

const { Provider } = AuthStore;

export const AuthProvider = ({ children }: any) => {
  const [authState, dispatch] = useReducer(auth, {
    init: true,
    loading: false,
  });
  const [persona, setPersona] = useState<string>(available_personas.data.find((ap: any) => ap.default)?.key || 'persona-hw');
  let navigate = useNavigate();
  const { pathname } = useLocation();

  const redirectToLogin = useCallback(() => {
    let { from } = { from: { pathname } };

    if ([LOGIN_ROUTE, THEME_ROUTE].indexOf(from.pathname) === -1) {
      navigate(LOGIN_ROUTE);
    }
  }, [navigate, pathname]);

  const redirectToPrivateHome = useCallback(() => {
    let { from } = { from: { pathname } };

    if (from.pathname === '/login') {
      navigate(DASHBOARD_ROUTE);
    }
  }, [navigate, pathname]);

  const initialLogin = useCallback(async () => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_INIT });
    try {
      const result = await checkLoginAPI();

      if (!result) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAIL,
          payload: { error: '' },
        });

        redirectToLogin();

        return;
      }

      const { email, role } = result;
      if (email) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: email, role },
        });

        redirectToPrivateHome();
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAIL,
          payload: { error: '' },
        });

        redirectToLogin();
      }
    } catch (e) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAIL,
        payload: { error: e },
      });
      console.log(e);
    }
  }, [redirectToLogin, redirectToPrivateHome]);

  const login = useCallback(
    async (email: string, password: string) => {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      try {
        const result = await loginAPI(email, password);

        const { user, message } = result;
        if (user) {
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user: user?.email, role: user?.role },
          });

          redirectToPrivateHome();
        } else {
          dispatch({
            type: AUTH_ACTIONS.LOGIN_FAIL,
            payload: { error: message },
          });
        }
      } catch (e) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAIL,
          payload: { error: e },
        });
        console.log(e);
      }

      return;
    },
    [redirectToPrivateHome]
  );

  const logout = async () => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      await logoutAPI();
      dispatch({
        type: AUTH_ACTIONS.LOGOUT_SUCCESS,
        payload: null,
      });

      redirectToLogin();
    } catch (e) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAIL,
        payload: { error: e },
      });
      console.log(e);
    }
  };

  const changePersona = (value: string) => {
    setPersona(value);
  };

  useEffect(() => {
    initialLogin();
  }, [initialLogin]);

  return (
    <Provider value={[authState, login, logout, persona, changePersona]}>
      {children}
    </Provider>
  );
};
